import { useTranslation } from '@/app/providers';
import { Skeleton } from '@/shared/components/skeleton';

const STEP_COUNT = 5;
const CALENDAR_CELL_COUNT = 35;

/**
 * Layout-shaped loading state for the public booking form.
 * Used for both lazy-route Suspense and form-details API loading so the
 * user sees one continuous skeleton instead of two centered spinners.
 */
export function PublicBookingFormSkeleton() {
  const { t } = useTranslation();

  return (
    <div
      className="min-h-screen bg-content-bg"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('publicBookingForm.loading')}
    >
      <div className="relative mx-auto min-h-screen w-full max-w-xl">
        <div className="bg-card shadow-sm">
          <header className="text-center">
            <div className="relative">
              <div className="relative flex h-56 w-full flex-col items-center justify-center gap-2 overflow-hidden bg-primary px-8 sm:h-72">
                <Skeleton className="absolute left-3 top-3 z-20 h-9 w-9 rounded-md bg-white/25" />
                <Skeleton className="h-3.5 w-24 rounded-md bg-white/30" />
                <Skeleton className="h-7 w-40 rounded-md bg-white/35 sm:h-8 sm:w-48" />
                <Skeleton className="mt-1 h-3.5 w-52 max-w-[80%] rounded-md bg-white/25" />
                <Skeleton className="h-3.5 w-28 rounded-md bg-white/25" />
              </div>

              <div className="absolute -bottom-12 left-1/2 z-10 flex h-24 w-24 -translate-x-1/2 items-center justify-center overflow-hidden rounded-full bg-white p-1 shadow-lg ring-1 ring-slate-200">
                <Skeleton variant="circular" className="h-full w-full" />
              </div>
            </div>
            <div className="pb-4 pt-14" aria-hidden />
          </header>

          <div className="space-y-0">
            <div className="border-b border-border px-4 py-3">
              <div className="flex items-center gap-3 rounded-xs bg-amber-50 px-3 py-3">
                <Skeleton variant="circular" className="h-9 w-9 shrink-0" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-40 max-w-full rounded-md" />
                  <Skeleton className="h-3 w-28 max-w-full rounded-md" />
                </div>
                <Skeleton className="h-4 w-4 shrink-0 rounded-md" />
              </div>
            </div>
            <div className="border-b border-border px-4 py-3">
              <div className="flex items-center gap-3 rounded-xs bg-emerald-50 px-3 py-3">
                <Skeleton variant="circular" className="h-9 w-9 shrink-0" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-36 max-w-full rounded-md" />
                  <Skeleton className="h-3 w-24 max-w-full rounded-md" />
                </div>
              </div>
            </div>
          </div>

          <div className="border-b border-border px-4 pb-3">
            <div className="flex items-start justify-between gap-2">
              {Array.from({ length: STEP_COUNT }, (_, index) => (
                <div
                  key={index}
                  className="flex min-w-0 flex-1 flex-col items-center gap-2 py-3"
                >
                  <Skeleton variant="circular" className="h-11 w-11" />
                  <Skeleton className="h-3 w-10 rounded-md" />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4 p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Skeleton variant="circular" className="h-3 w-3" />
                <Skeleton className="h-3 w-20 rounded-md" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton variant="circular" className="h-5 w-5" />
                <Skeleton className="h-3 w-24 rounded-md" />
              </div>
            </div>

            <div className="rounded-xs border border-border bg-card p-3">
              <div className="mb-4 flex items-center justify-between">
                <Skeleton className="h-8 w-8 rounded-md" />
                <Skeleton className="h-5 w-36 rounded-md" />
                <Skeleton className="h-8 w-8 rounded-md" />
              </div>
              <div className="mb-2 grid grid-cols-7 gap-1">
                {Array.from({ length: 7 }, (_, index) => (
                  <Skeleton
                    key={`dow-${String(index)}`}
                    className="mx-auto h-3 w-6 rounded-md"
                  />
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: CALENDAR_CELL_COUNT }, (_, index) => (
                  <Skeleton
                    key={`day-${String(index)}`}
                    className="aspect-square w-full rounded-md"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <span className="sr-only">{t('publicBookingForm.loading')}</span>
    </div>
  );
}
